import React, { useId, useState, useRef, useEffect, useContext } from 'react';
import { GeolocationType } from '../../../helpers/global-storage';
import { ExtendedWindowType, headlessEngine } from '../../engine';
import {
  loadAdvancedSearchQueryActions,
  loadSearchActions,
  loadSearchAnalyticsActions,
  loadContextActions,
  ContextPayload,
  SearchEngine
} from '@coveo/headless';
import { fieldsObject, labelsObj } from '../../controllers/controller';
import ZipForm from '../../../react-components/zip-form';
import { COVEO_PAGINATION_EVENTS } from '../../components/pagination';
import {
  getUrlParamValue,
  setUrlParamValue
} from '../../../helpers/queryString';
import {
  getLatLongFromZip,
  updateGeolocationDataProperties
} from '../../../helpers/geolocation';
import { MASTHEAD_ZIP_FORM_EVENTS } from '../../../react-components/contractor-locator-listing/helpers';
import { getAdvancedQueryParts } from '../../../helpers/transform';
import SecondCall from '../../components/second-call-context';

export const DISTANCE_PLACEHOLDERS = Object.freeze({
  zipCode: '{{zipCode}}',
  country: '{{country}}'
});

type zipRegexProps = {
  [key: string]: string;
};

type DistanceProps = {
  title: string;
  invalidZipMessage: string;
  endpoint: string;
  redirectUrl: string;
  latitudeField: string;
  longitudeField: string;
  values: {
    value: number;
    label: string;
    default: string;
  }[];
  autofill: boolean;
  withQueryParams: boolean;
  shouldRedirect?: boolean;
  redirect?: string;
};

const { dataset } = document.getElementsByClassName(
  'coveo-listing__data-wrapper'
)[0] as HTMLElement;

const geolocation = JSON.parse(
  dataset.geolocation as string
) as GeolocationType;

const zipRegex = dataset.zipRegex
  ? JSON.parse(dataset.zipRegex)
  : ({} as zipRegexProps);

const distanceObj = dataset.distance
  ? (JSON.parse(dataset.distance) as DistanceProps)
  : ({} as DistanceProps);

const advancedQuerySearchActionCreator = loadAdvancedSearchQueryActions(
  headlessEngine as SearchEngine
);

type defaultCountryValueProps = {
  defaultCountryValue: string;
  shouldSearch?: boolean;
};

function DistanceFacet({
  defaultCountryValue,
  shouldSearch
}: defaultCountryValueProps) {
  const id = useId();
  const zipPlaceholders = {
    us: labelsObj.usZipPlaceholder,
    usValue: labelsObj.usValue,
    ca: labelsObj.caZipPlaceholder,
    caValue: labelsObj.caValue
  };

  const distanceFromUrl = getUrlParamValue('distance');

  const defaultDistanceValue =
    distanceFromUrl || distanceObj.values.find(value => value.default)?.value;

  const distanceRef = useRef<HTMLSelectElement>(null);

  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('');
  const [isReady, setIsReady] = useState(false);
  const [latLong, setLatLong] = useState<{
    latitude?: number;
    longitude?: number;
  }>({});
  const [shouldFirstSearch, setShouldFirstSearch] = useState(true);
  const secondCallContext = useContext(SecondCall);

  const executeDistanceFilter = () => {
    //Add distance and country to advanced query.
    const distance = distanceRef.current?.value;
    let newQuery = '';
    if (!distance) return;

    const previousQuery = (headlessEngine as SearchEngine).state
      .advancedSearchQueries?.aq;
    const countryOpt = country.includes('ca')
      ? labelsObj.caValue
      : labelsObj.usValue;

    if (previousQuery) {
      const parts = getAdvancedQueryParts(previousQuery);

      const newParts = parts.map(part => {
        if (part.includes(`@${fieldsObject.distanceInMiles}`)) {
          part = `@${fieldsObject.distanceInMiles} <= ${distance}`;
          return part.trim();
        }
        if (part.includes(`@${fieldsObject.countryCode}`)) {
          part = `@${fieldsObject.countryCode} = ${countryOpt}`;
          return part.trim();
        }

        return part.trim();
      });

      newQuery = newParts.join(' ');

      if (!newQuery.includes(`@${fieldsObject.distanceInMiles}`) && distance) {
        newQuery = `${previousQuery} AND @${fieldsObject.distanceInMiles} <= ${distance}`;
      }

      if (!newQuery.includes(`@${fieldsObject.countryCode}`) && countryOpt) {
        newQuery = `${newQuery} AND @${fieldsObject.countryCode} = ${countryOpt}`;
      }
    } else {
      newQuery = ` @${fieldsObject.distanceInMiles} <= ${distance} AND @${fieldsObject.countryCode} = ${countryOpt}`;
    }

    (headlessEngine as SearchEngine)?.dispatch(
      advancedQuerySearchActionCreator.registerAdvancedSearchQueries({
        aq: newQuery
      })
    );

    const { logInterfaceLoad, logInterfaceChange } = loadSearchAnalyticsActions(
      headlessEngine as SearchEngine
    );
    const { executeSearch } = loadSearchActions(headlessEngine as SearchEngine);

    if (shouldFirstSearch && shouldSearch) {
      (headlessEngine as SearchEngine).dispatch(
        executeSearch(logInterfaceLoad())
      );
      setShouldFirstSearch(false);
    } else if (!shouldFirstSearch) {
      (headlessEngine as SearchEngine).dispatch(
        executeSearch(logInterfaceChange())
      );
    }

    setUrlParamValue('distance', distance || '');
  };

  const initZipValue = async () => {
    setCountry(geolocation.country);
    setZip(geolocation.zip);
    setLatLong({
      latitude: geolocation.latitude,
      longitude: geolocation.longitude
    });

    setTimeout(() => {
      window.dispatchEvent(new Event(MASTHEAD_ZIP_FORM_EVENTS.ready));
      setIsReady(true);
    }, 100);
  };

  const updateDistanceQuery = () => {
    const { latitude, longitude } = latLong;

    if (latitude && longitude) {
      const global = window as ExtendedWindowType;

      global.preProcessRequest = (body: string) => {
        const newBody = JSON.parse(body.toString());
        newBody.queryFunctions = [
          {
            fieldName: `@${fieldsObject.distanceInMiles}`,
            function: `dist(@${distanceObj.latitudeField}, @${distanceObj.longitudeField}, ${latitude}, ${longitude})*0.000621371`
          }
        ];
        return JSON.stringify(newBody);
      };
      executeDistanceFilter();
    }
  };

  useEffect(() => {
    window.addEventListener('zipFormSubmit', () => {
      setIsReady(true);
    });
    initZipValue();
  }, []);

  useEffect(() => {
    if (latLong.latitude && latLong.longitude) {
      updateDistanceQuery();
    }
  }, [latLong]);

  const handleFormSubmit = async (zip: string, country: string) => {
    const latLong = await getLatLongFromZip(zip, country);
    updateGeolocationDataProperties({
      latitude: latLong.latitude,
      longitude: latLong.longitude,
      zip,
      country
    });

    if (distanceObj.redirectUrl && zip && country) {
      window.location.href = distanceObj.redirectUrl
        .replace(DISTANCE_PLACEHOLDERS.zipCode, zip)
        .replace(DISTANCE_PLACEHOLDERS.country, country);
    }
  };

  const handleDistanceChange = () => {
    window.dispatchEvent(new Event(COVEO_PAGINATION_EVENTS.reset));
    if (secondCallContext.originalContext !== undefined) {
      const { setContext } = loadContextActions(headlessEngine as SearchEngine);
      headlessEngine?.dispatch(
        setContext({
          sortingStrategy: secondCallContext.originalContext
        } as ContextPayload)
      );
    }
    executeDistanceFilter();

    const global = window as ExtendedWindowType;

    global.dataLayer?.push({
      event: 'contractor_distance_filter_selected',
      event_category: 'find a contractor',
      event_action: 'distance filter',
      event_label: `user selected | ${distanceRef.current?.value} mi`,
      event_attributes: {
        zip_code: zip
      }
    });
  };

  return (
    <section
      className="distance-facet"
      aria-labelledby={`distance-facet-heading${id}`}
    >
      <h2
        id={`distance-facet-heading${id}`}
        className="distance-facet__heading"
      >
        {distanceObj.title}
      </h2>
      {isReady && (
        <ZipForm
          zipPlaceholders={zipPlaceholders}
          defaultCountryValue={
            country || defaultCountryValue?.toLowerCase() || 'us'
          }
          defaultZipValue={zip}
          invalidZipMessage={distanceObj.invalidZipMessage}
          zipRegex={zipRegex}
          onSubmit={handleFormSubmit}
        />
      )}
      <div className="field field--select distance-facet__distance">
        <label htmlFor={`select-distance${id}`} className="field__label">
          Distance
        </label>
        <select
          ref={distanceRef}
          defaultValue={defaultDistanceValue}
          name={`select-distance${id}`}
          id={`select-distance${id}`}
          className="field__select"
          onChange={handleDistanceChange}
        >
          {distanceObj.values.map((value, index) => (
            <option key={index} value={value.value}>
              {value.label}
            </option>
          ))}
        </select>
      </div>
    </section>
  );
}

export default DistanceFacet;
